import { MRT_ColumnDef } from 'mantine-react-table'
import { Avatar, Text, Tooltip } from '@mantine/core'
import { ReactNode } from 'react'
import { FacilityParams } from 'app/models/facility-params'
import getInitials from 'app/views/components/functions/getInitials'
import FacilityActions from './facility-actions'
import StatusPill from '../../common/pill-status'
import { formatDateToShow, formatNumberToCurrency, stringToDate } from 'app/utils/util-functions'
import { NonProRataIconWithTooltip } from 'app/views/components/icons/NonProRataIconWithTooltip'
import FacilityHoldIndicator from 'app/views/components/facility-hold-indicator'

function TableHead({ header }: { header: string }) {
  return (
    <Text
      style={{
        fontFamily: 'Plus Jakarta Sans',
        fontSize: '1.5em',
      }}
    >
      {header}
    </Text>
  )
}

function TableRowText({ text }: { text: ReactNode }) {
  return (
    <Text
      style={{
        fontFamily: 'Plus Jakarta Sans',
        fontSize: '1.5em',
      }}
    >
      {text}
    </Text>
  )
}

export default function getColumnDefs(): MRT_ColumnDef<FacilityParams>[] {
  return [
    {
      size: 260,
      accessorFn: row => row.dealName,
      accessorKey: 'dealName',
      header: 'Deal Name',
      Header: <TableHead header={'Deal Name'} />,
      Cell: ({ cell }) => (
        <TableRowText text={cell.getValue<string>()} />
      ),
    },
    {
      size: 200,
      accessorKey: 'name',
      header: 'Name',
      Header: <TableHead header={'Name'} />,
      Cell: ({ cell }) => (
        <TableRowText text={cell.getValue<string>()} />
      ),
    },
    {
      size: 130,
      accessorKey: 'facilityType',
      header: 'Facility Type',
      Header: <TableHead header={'Facility Type'} />,
      Cell: ({ cell }) => (
        <TableRowText text={cell.getValue<string>()} />
      ),
    },
    {
      size: 120,
      accessorKey: 'status',
      header: 'Status',
      Header: <TableHead header={'Status'} />,
      Cell: cell => {
        const aditionalStatus = cell.row.original.amendment
          ? cell.row.original.amendmentType ?? 'Amendments'
          : ''
        return (
          <StatusPill
            state={cell.row.original.status ?? 'Draft'}
            additionalState={aditionalStatus}
          />
        )
      },
    },
    {
      accessorKey: 'amount',
      header: 'Amount',
      Header: <TableHead header={'Amount'} />,
      filterVariant: 'range',
      Cell: ({ cell, row }: any) => (
        <TableRowText
          text={formatNumberToCurrency(cell.getValue(), row.original.baseCurrency ?? 'USD')}
        />
      ),
    }, {
      accessorKey: 'utilizedAmount',
      header: 'Utilized Amount',
      Header: <TableHead header={'Utilized Amount'} />,
      filterVariant: 'range',
      Cell: ({ cell, row }: any) => (
        <TableRowText
          text={formatNumberToCurrency(cell.getValue(), row.original.baseCurrency ?? 'USD')}
        />
      ),
    },
    {
      accessorKey: 'startDate',
      header: 'Start Date',
      Header: <TableHead header={'Start Date'} />,
      accessorFn: row => {
        return stringToDate(row.startDate);
      },
      mantineFilterTextInputProps: {
        placeholder: 'Date',
      },
      filterFn: 'lessThanOrEqualTo',
      filterVariant: 'date',
      sortingFn: 'datetime',
      Cell: ({ cell }) => (
        <Text fz="1.5em">{formatDateToShow(cell.getValue<Date>())}</Text>
      ), //render Date as a string
    },
    {
      accessorKey: 'maturity',
      header: 'Maturity Date',
      Header: <TableHead header={'Maturity Date'} />,
      accessorFn: row => {
        return stringToDate(row.maturity);
      },
      mantineFilterTextInputProps: {
        placeholder: 'Date',
      },
      filterFn: 'greaterThanOrEqualTo',
      filterVariant: 'date',
      sortingFn: 'datetime',
      Cell: ({ cell }) => (
        <Text fz="1.5em">{formatDateToShow(cell.getValue<Date>())}</Text>
      ), //render Date as a string
    },
    {
      size: 100,
      accessorFn: row => row.adminEntityName,
      accessorKey: 'adminEntityName',
      header: 'Admin',
      Header: <TableHead header={'Admin'} />,
      Cell: ({ cell }) => {
        return (
          <Tooltip label={cell.getValue<string>()}>
            <Avatar radius="xl" color="cyan" size="lg">
              {getInitials(cell.getValue<string>() as string)}
            </Avatar>
          </Tooltip>
        )
      },
    },
    {
      size: 130,
      accessorKey: 'alerts',
      header: 'Alerts',
      Header: <TableHead header={'Alerts'} />,
      Cell: ({ row }) => {
        const facility = row.original
        const hasNonProRataLoans = facility.hasNonProRataLoans === true

        return (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <FacilityHoldIndicator facility={facility} enableLift />
            {hasNonProRataLoans && <NonProRataIconWithTooltip />}
          </div>
        )
      },
    },
    {
      size: 130,
      accessorKey: 'clientId',
      header: 'Client ID',
      Header: <TableHead header={'Client ID'} />,
      Cell: ({ cell }) => (
        <TableRowText text={cell.getValue<string>()} />
      ),
    },
    {
      size: 150,
      accessorKey: 'id',
      header: 'Actions',
      Header: <TableHead header={'Actions'} />,
      enableColumnActions: false,
      enableColumnFilter: false,
      Cell: cell => <FacilityActions row={cell.row.original} />,
    },
    //System ID is a hidden column and only used for Export to CSV
    {
      accessorKey: 'systemId',
      header: 'System ID',
      enableHiding: false,   // prevents toggling
      enableColumnFilter: false,
      enableSorting: false,
      enableColumnActions: false,
      Cell: () => null,
      Header: () => null,
    }
  ]
}
